import { useCallback } from 'react';
import { cloneDeep } from 'lodash';
import useCancelableRequest from './useCancelableRequest';
import { BookingConfigEnum } from '../../components/BookingWidget/bookingSteps.interface';
import { CreatePriceResponse } from '../interfaces/plugAndSell/createPricingResponse';
import getPricing from './pricingData.service';
import createQuote from './quoteData.service';
import { getToken } from '../helpers/auth.helper';
import updatePriceInBookingSteps from '../helpers/updatePriceInBookingSteps';
import {
  LeaveStepResult,
  PricingParams,
} from '../interfaces/usePriceManager.interface';

function usePricingManager(): {
  processPricing: (params: PricingParams) => Promise<LeaveStepResult>;
} {
  const runCancelable = useCancelableRequest();

  const createQuotePricing = useCallback(
    async (params: PricingParams): Promise<LeaveStepResult> => {
      const { bookingSteps, bookingConfigData } = params;
      const apiCM360Url =
        bookingConfigData[BookingConfigEnum.DataCenter].cm360Endpoint;
      const pricingResponse = await getPricing({
        ...params,
        cancelToken: undefined,
      });

      const { version } = pricingResponse;
      const decoded = getToken();
      const bookingStepsWithUpdatedPricing = updatePriceInBookingSteps(
        bookingSteps,
        pricingResponse.products,
      );

      const quoteResponse = await createQuote(
        apiCM360Url,
        version,
        bookingStepsWithUpdatedPricing,
        params.bookingConfigData,
        { agentEmail: decoded?.email },
        params.bookingDataResponse,
      );

      return { pricingResponse, quoteResponse };
    },
    [],
  );

  const updateAddonPricing = useCallback(
    async (params: PricingParams): Promise<CreatePriceResponse> =>
      runCancelable((token) =>
        getPricing({
          ...params,
          cancelToken: token,
        }),
      ),
    [runCancelable],
  );

  const processPricing = useCallback(
    async (params: PricingParams): Promise<LeaveStepResult> => {
      const snapshotParams: PricingParams = cloneDeep(params);

      if (params.isRunWithQuoteCreation) {
        return createQuotePricing(snapshotParams);
      }
      const pricingResponse = await updateAddonPricing(snapshotParams);
      return { pricingResponse };
    },
    [createQuotePricing, updateAddonPricing],
  );

  return { processPricing };
}

export default usePricingManager;
